import React, { memo, useMemo } from 'react';
import { useConversationStarters } from '../../utils/hooks';
import { PageContext } from '../hooks/usePageContext';
import {
  ConciergeHomeLayoutWidget,
  ConciergeHomeLogoWidget,
  ConciergeHomeBrandLabelWidget,
  ConciergeHomeGreetingWidget,
  ConciergeHomeSubtitleWidget,
  ConciergeHomeStartersWidget,
  useWidgetConfig,
} from '@brandclub/bc-widgets';
import type { StartersMode } from '@brandclub/bc-widgets';
import { PromptInputConcierge } from './PromptInputConcierge';

interface ConciergeWelcomeFullPageProps {
  displayQueries: string[];
  isLoadingSuggestedQueries: boolean;
  onSearch: (searchTerm: string) => void;
  pageContext?: PageContext;
  apiBaseUrl?: string;
  brandLogoUrl?: string;
  brandName?: string;
  userName?: string;
  conciergeName?: string;
  // Input state -- renders the search input as part of the centered home content
  searchInputValue?: string;
  setSearchInputValue?: (value: string) => void;
  handleSearch?: (searchTerm: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => void;
  isActivelyStreaming?: boolean;
  // Mobile: when true, input and starters are excluded from center content
  isMobile?: boolean;
  // Voice mode (Plan 1 / Plan 7a) — when enabled, render the "Call
  // {assistantName}" CTA in the prompt input. Click handler is supplied
  // by the host; today it calls `ConciergeContextProvider.openVoiceCall()`
  // which mounts `<VoiceCallOverlay>` full-screen above the unified
  // `/concierge?conversationId=:id` route (D-63).
  voiceCallEnabled?: boolean;
  onVoiceCallClick?: () => void;
  voiceAssistantName?: string;
}

const ConciergeWelcomeFullPageComponent: React.FC<ConciergeWelcomeFullPageProps> = ({
  displayQueries,
  isLoadingSuggestedQueries,
  onSearch,
  pageContext,
  apiBaseUrl,
  brandLogoUrl,
  brandName = '',
  userName,
  conciergeName = 'Concierge',
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  isMobile: isMobileProp = false,
  voiceCallEnabled = false,
  onVoiceCallClick,
  voiceAssistantName,
}) => {
  const pageType = pageContext?.pageType;
  const stacklineSku = pageContext?.stacklineSku;

  const chatInputConfig = useWidgetConfig<{ placeholderDefault?: string }>('concierge-chat-input', {});
  const inputPlaceholder = chatInputConfig.placeholderDefault || 'Ask me anything';

  // Fetch context-specific queries using the shared hook
  const { questions: contextQueries, isLoading: isLoadingContextQueries } = useConversationStarters({
    stacklineSku,
    apiBaseUrl,
    source: pageContext?.source,
    enabled: pageType === 'pdp' && !!stacklineSku,
  });

  // Use context queries if available, otherwise fall back to displayQueries
  const queriesToShow = useMemo(
    () => (contextQueries.length > 0 ? contextQueries : displayQueries),
    [contextQueries, displayQueries],
  );
  const isLoading = useMemo(
    () => isLoadingContextQueries || (pageContext?.pageType === 'pdp' && isLoadingSuggestedQueries),
    [isLoadingContextQueries, pageContext?.pageType, isLoadingSuggestedQueries],
  );

  return (
    <ConciergeHomeLayoutWidget widgetId="concierge-home-layout" displayName="Home Layout">
      {(layoutState, resolveColor) => {
        // Resolve mobile overrides
        const isMobile = isMobileProp || (typeof window !== 'undefined' && window.innerWidth < 768);
        const logoPos =
          isMobile && layoutState.mobile_logoPosition !== 'inherit'
            ? layoutState.mobile_logoPosition
            : layoutState.logoPosition;
        const startersMode: StartersMode =
          isMobile && layoutState.mobile_startersMode !== 'inherit'
            ? (layoutState.mobile_startersMode as StartersMode)
            : layoutState.startersMode;

        // Resolve background
        const bgColor =
          layoutState.backgroundStyle === 'colored' ? resolveColor(layoutState.backgroundColor) : '#ffffff';

        // Starters element (shared between desktop and mobile positions)
        const startersElement =
          startersMode !== 'hidden' && queriesToShow.length > 0 && !isLoading ? (
            <ConciergeHomeStartersWidget
              mode={startersMode}
              elementStyle={layoutState.elementStyle}
              cardLayoutDirection={layoutState.cardLayoutDirection}
              maxStarters={layoutState.maxStarters}
              queries={queriesToShow}
              onSearch={onSearch}
            />
          ) : null;

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              flex: 1,
              minHeight: 0,
              boxSizing: 'border-box',
              // `overflow: hidden` on the mobile column made anything that did
              // not fit unreachable — at 200% browser zoom (WCAG 1.4.4/1.4.10)
              // the viewport is short enough that the starters and composer got
              // clipped with no way to scroll to them, and attaching an image
              // made it worse by growing the composer. `hidden auto` keeps the
              // no-horizontal-scroll intent and adds a vertical escape hatch,
              // which stays inert whenever the content does fit.
              overflow: isMobile ? 'hidden auto' : 'auto',
              backgroundColor: bgColor,
              position: 'relative',
            }}
          >
            {/* Top spacer pushes content toward center */}
            <div style={{ flex: 1 }} />

            {/* ========== Center content area ========== */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: isMobile ? '16px 16px' : '24px 16px',
                flexShrink: 0,
              }}
            >
              {/* Hero area */}
              {logoPos === 'hero' && (
                <>
                  {layoutState.brandLabelPosition === 'above-hero-logo' && <ConciergeHomeBrandLabelWidget />}
                  <ConciergeHomeLogoWidget logoUrl={brandLogoUrl} />
                  {layoutState.subtitlePosition === 'under-hero-logo' && (
                    <ConciergeHomeSubtitleWidget brandName={brandName} conciergeName={conciergeName} />
                  )}
                </>
              )}

              {/* Greeting area */}
              {layoutState.showGreeting && (
                <ConciergeHomeGreetingWidget
                  userName={userName}
                  showIcon={layoutState.showGreetingIcon}
                  iconPosition={layoutState.greetingIconPosition}
                />
              )}

              {/* Subtitle under greeting */}
              {layoutState.subtitlePosition === 'under-greeting' && (
                <ConciergeHomeSubtitleWidget brandName={brandName} conciergeName={conciergeName} />
              )}

              {/* Search input -- desktop only (mobile renders it at the bottom via parent) */}
              {!isMobile && handleSearch && setSearchInputValue && (
                <div style={{ width: '100%', maxWidth: 768, marginTop: layoutState.searchInputGap ?? 24 }}>
                  <PromptInputConcierge
                    placeholder={inputPlaceholder}
                    value={searchInputValue ?? ''}
                    onChange={setSearchInputValue}
                    onSubmit={handleSearch}
                    isStreaming={isActivelyStreaming}
                    showCallButton={voiceCallEnabled && !!onVoiceCallClick}
                    onCallClick={onVoiceCallClick}
                    voiceAssistantName={voiceAssistantName}
                  />
                </div>
              )}

              {/* Conversation starters -- desktop only (mobile renders them above input) */}
              {!isMobile && startersElement}

              {/* Logo after content (not pinned to bottom) -- desktop only */}
              {!isMobile && logoPos === 'after-content' && (
                <div
                  style={{
                    marginTop: layoutState.footerGap,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {layoutState.brandLabelPosition === 'above-footer-logo' && <ConciergeHomeBrandLabelWidget />}
                  <ConciergeHomeLogoWidget logoUrl={brandLogoUrl} />
                </div>
              )}
            </div>

            {/* Bottom spacer - less than top to bias content slightly upward */}
            <div style={{ flex: isMobile ? 1 : 1.2 }} />

            {/* ========== Mobile: Starters + Input at bottom (inside bg color) ========== */}
            {isMobile && startersElement && (
              <div
                style={{
                  flexShrink: 0,
                }}
              >
                {startersElement}
              </div>
            )}

            {/* Mobile: Input at the bottom, inside the colored background */}
            {isMobile && handleSearch && setSearchInputValue && (
              <div
                style={{
                  flexShrink: 0,
                  paddingTop: 16,
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
                }}
              >
                <PromptInputConcierge
                  placeholder="Ask me anything"
                  value={searchInputValue ?? ''}
                  onChange={setSearchInputValue}
                  onSubmit={handleSearch}
                  isStreaming={isActivelyStreaming}
                  showCallButton={voiceCallEnabled && !!onVoiceCallClick}
                  onCallClick={onVoiceCallClick}
                  voiceAssistantName={voiceAssistantName}
                  forceMobile
                />
              </div>
            )}

            {/* ========== Footer area (desktop only) ========== */}
            {!isMobile && logoPos === 'footer' && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: layoutState.footerGap,
                  pointerEvents: 'none',
                }}
              >
                <div style={{ pointerEvents: 'auto' }}>
                  {layoutState.brandLabelPosition === 'above-footer-logo' && <ConciergeHomeBrandLabelWidget />}
                  <ConciergeHomeLogoWidget logoUrl={brandLogoUrl} />
                </div>
              </div>
            )}
          </div>
        );
      }}
    </ConciergeHomeLayoutWidget>
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
export const ConciergeWelcomeFullPage = memo(ConciergeWelcomeFullPageComponent);
